/*
 * @Author: songjingyang songjingyang@meishubao.com
 * @Date: 2025-03-28 15:40:04
 * @LastEditors: songjiangyang songjingyang@meishubao.com
 * @LastEditTime: 2026-04-27 18:58:59
 * @FilePath: src/store/modules/permission.ts
 * @Description: TypeScript版本 - 自动转换
 */

import { defineStore } from 'pinia'

import auth from '@/plugins/auth'
import router, { constantRoutes, dynamicRoutes } from '@/router'
import { getRouters } from '@/api/menu'
import Layout from '@/layout/index'
import ParentView from '@/components/ParentView'
import InnerLink from '@/layout/components/InnerLink'
import useUserStore from '@/store/modules/user'
import { translate } from '@/locales/route'
import { institutionAssetRoutes } from '@/router/institutionAssets'
import { canViewInstitutionAssets, isInstitutionUser } from '@/composables/useInstitutionAssets'

import { RouteRecordRaw } from 'vue-router'

// 匹配views里面所有的.vue文件
const modules = import.meta.glob('./../../views/**/*.vue')

const usePermissionStore = defineStore('permission', {
  state: (): any => ({
    routes: [],
    addRoutes: [],
    defaultRoutes: [],
    topbarRouters: [],
    sidebarRouters: [],
  }),
  actions: {
    setRoutes(routes: any): void {
      this.addRoutes = routes
      this.routes = constantRoutes.concat(routes)
    },
    setDefaultRoutes(routes: any): void {
      this.defaultRoutes = constantRoutes.concat(routes)
    },
    setTopbarRoutes(routes: any): void {
      this.topbarRouters = routes
    },
    setSidebarRouters(routes: any): void {
      // Apply requested module moves while preserving all other relative ordering.
      // Use this same order for navigation and the permission-based login landing page.
      const assetPaths = institutionAssetRoutes.map(route => route.path)
      const orderedRoutes = routes.filter(item => !assetPaths.includes(item.path))
      if (canViewInstitutionAssets()) {
        orderedRoutes.push(...institutionAssetRoutes.map(route => ({ ...route, hidden: false })))
      }
      if (isInstitutionUser()) {
        const institutionIndex = orderedRoutes.findIndex(item => item.path === '/institution')
        if (institutionIndex !== -1) {
          const [institution] = orderedRoutes.splice(institutionIndex, 1)
          orderedRoutes.push({ ...institution, meta: { ...institution.meta, institutionSettings: true } })
        }
      }
      const applicationIndex = orderedRoutes.findIndex(item => item.path === '/application' && !item.hidden)
      const functionIndex = orderedRoutes.findIndex(item => item.path === '/function' && !item.hidden)
      if (applicationIndex !== -1 && functionIndex !== -1) {
        const [application] = orderedRoutes.splice(applicationIndex, 1)
        const targetIndex = orderedRoutes.findIndex(item => item.path === '/function' && !item.hidden)
        orderedRoutes.splice(targetIndex, 0, application)
      }
      const systemIndex = orderedRoutes.findIndex(item => item.path === '/system' && !item.hidden)
      if (systemIndex !== -1) {
        const [system] = orderedRoutes.splice(systemIndex, 1)
        orderedRoutes.push(system)
      }
      this.sidebarRouters = orderedRoutes.map((v: { path: string; children: any[] }) => {
        if (v.path === '/institution' && useUserStore().orgId !== 0) {
          v.children = v.children.map((vv: { path: string; meta: any }) => ({
            ...vv,
            hidden: vv.path === 'list',
            meta: {
              ...vv.meta,
              title: translate('route.institutionInfo'),
              titleKey: 'route.institutionInfo'
            },
          }))
        }
        return {
          ...v,
        }
      }).filter((item: any) => !item.meta?.institutionSettings || item.children?.some((child: any) => !child.hidden))
    },
    generateRoutes(roles: any): void {
      return new Promise((resolve, reject) => {
        // 向后端请求路由数据
        getRouters().then((res: { data: any }) => {
          const sdata = JSON.parse(JSON.stringify(res.data))
          const rdata = JSON.parse(JSON.stringify(res.data))
          const defaultData = JSON.parse(JSON.stringify(res.data))
          const sidebarRoutes = filterAsyncRouter(sdata)
          const rewriteRoutes = filterAsyncRouter(rdata, false, true)
          const defaultRoutes = filterAsyncRouter(defaultData)
          const asyncRoutes = filterDynamicRoutes(dynamicRoutes)
          asyncRoutes.forEach((route: RouteRecordRaw) => {
            router.addRoute(route)
          })
          this.setRoutes(rewriteRoutes)
          this.setSidebarRouters(constantRoutes.concat(sidebarRoutes))
          this.setDefaultRoutes(sidebarRoutes)
          this.setTopbarRoutes(defaultRoutes)
          resolve(rewriteRoutes)
        }).catch(reject)
      })
    },
  },
})

// 遍历后台传来的路由字符串，转换为组件对象
function filterAsyncRouter(asyncRouterMap: any, lastRouter: any, type: any): any {
  return asyncRouterMap.filter((route: { [x: string]: any; children: string | any[] | null; component: any; path: any }) => {
    if (type && route.children) {
      route.children = filterChildren(route.children)
    }
    if (route.component) {
      // Layout ParentView 组件特殊处理
      const componentName = route.component
      if (componentName === 'Layout') {
        route.component = Layout
      } else if (componentName === 'ParentView') {
        route.component = ParentView
      } else if (componentName === 'InnerLink') {
        route.component = InnerLink
      } else {
        route.component = loadView(componentName)
        // 后端配置的组件路径无法匹配到 src/views 下任何 .vue 文件时，给出明确提示
        if (!route.component) {
          console.warn(`[router] 找不到视图文件 src/views/${componentName}.vue，对应路由 "${route.path}" 将被忽略，请检查后端菜单配置或文件大小写`)
        }
      }
    }
    if (route.children != null && route.children && route.children.length) {
      route.children = filterAsyncRouter(route.children, route, type)
    } else {
      delete route['children']
      delete route['redirect']
    }
    // 兜底：丢弃既无 component 也无 children 的脏路由，避免 Vue Router 抛出 "missing component(s) or children" 警告
    const hasComponent = !!route.component
    const hasChildren = Array.isArray(route.children) && route.children.length > 0
    if (!hasComponent && !hasChildren) {
      console.warn(`[router] 跳过无效路由记录 "${route.path}"：既无 component 也无 children，请检查后端菜单配置`)
      return false
    }
    return true
  })
}

function filterChildren(childrenMap: any, lastRouter: any): any {
  var children: any[] = []
  childrenMap.forEach((el: { children: any[]; component: string; path: string }, index: any) => {
    if (el.children && el.children.length) {
      if (el.component === 'ParentView' && !lastRouter) {
        el.children.forEach((c: { path: string; children: string | any[] }) => {
          c.path = el.path + '/' + c.path
          if (c.children && c.children.length) {
            children = children.concat(filterChildren(c.children, c))
            return
          }
          children.push(c)
        })
        return
      }
    }
    if (lastRouter) {
      el.path = lastRouter.path + '/' + el.path
      if (el.children && el.children.length) {
        children = children.concat(filterChildren(el.children, el))
        return
      }
    }
    children = children.concat(el)
  })
  return children
}

// 动态路由遍历，验证是否具备权限
export function filterDynamicRoutes(routes: any): any {
  const res: any[] = []
  routes.forEach((route: { permissions: any; roles: any }) => {
    if (route.permissions) {
      if (auth.hasPermiOr(route.permissions)) {
        res.push(route)
      }
    } else if (route.roles) {
      if (auth.hasRoleOr(route.roles)) {
        res.push(route)
      }
    }
  })
  return res
}

export const loadView = (view: any): any => {
  let res
  for (const path in modules) {
    const dir = path.split('views/')[1].split('.vue')[0]
    if (dir === view) {
      res = () => modules[path]()
    }
  }
  return res
}

export default usePermissionStore
